import { LockOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import SuperFetch from 'library/helpers/superFetch';
import { Fragment, useState } from 'react';
import { Loader, Notification } from 'zComponents/atoms';
import { Button, Divider, Flex, Form, Modal, Radio, Select, Tag, Tooltip, Typography } from 'zui/Antd';
import AddDefaultCategoryModal from '../AddDefaultCategoryModal';

const { Text } = Typography;

const AssignInventoryAssets = ({
	children,
	open,
	setOpen,
	workspaces,
	devices = [],
	setDevices,
	refetch,
	categories,
	platform = 'Control',
	type = 'Devices'
}) => {
	const {
		token: { marginXL }
	} = theme.useToken();

	const [saveLoading, setSaveLoading] = useState(false);
	const [openCreateCategoryModal, setOpenCreateCategoryModal] = useState(false);
	const [loadingSubcategories, setLoadingSubcategories] = useState(false);
	const [subCategoryType, setSubCategoryType] = useState([]);
	const [creatingCategory, setCreatingCategory] = useState(false);
	const isLoadingTemplates = loadingSubcategories || creatingCategory;

	const [form] = Form.useForm();

	const handleCancel = () => {
		setDevices([]);
		setOpen(false);
		form.resetFields();
		setSubCategoryType([]);
	};

	const fetchSubCategories = async (orgID, defaultCategoryID) => {
		setLoadingSubcategories(true);
		try {
			const res = await SuperFetch.get('/deviceCategories', {}, { defaultCategoryID }, orgID);
			setSubCategoryType(res.data ?? []);
		} catch (error) {
			Notification('error', error.message);
		} finally {
			setLoadingSubcategories(false);
		}
	};

	const handleChangeValues = (changedValues, allValues) => {
		const orgID = allValues.selectedSpace;
		const defaultCategoryID = allValues.categoryType;

		if ('selectedSpace' in changedValues) {
			form.setFieldsValue({
				categoryType: null,
				categoryID: null
			});
			setSubCategoryType([]);
		}

		if (orgID && defaultCategoryID) {
			setLoadingSubcategories(true);
			fetchSubCategories(orgID, defaultCategoryID);
		}
	};

	const handleSubmit = async () => {
		setSaveLoading(true);
		const { selectedSpace, categoryType, categoryID } = form.getFieldsValue();

		const body = {
			deviceIDs: devices.map((device) => device.inventoryUID),
			defaultCategoryID: categoryType,
			isControlPlatform: platform === 'Control',
			...(categoryID ? { categoryID } : null)
		};

		try {
			await SuperFetch.post('/inventories/assignAssets', body, {}, selectedSpace);
			Notification('success', `Devices assigned to the workspace.`);
			await refetch();
			handleCancel();
		} catch (error) {
			Notification('error', 'Error assigning devices. Please try again.');
		}

		setSaveLoading(false);
	};

	return (
		<Fragment>
			<Tooltip
				title={
					devices.length > 1
						? `Assign ${platform} ${type}s to organisation`
						: `Assign ${platform} ${type} to organisation`
				}
			>
				{children ? (
					children
				) : (
					<Button size='small' type='primary' onClick={() => setOpen(true)}>
						Assign
					</Button>
				)}
			</Tooltip>

			<Modal
				title={
					devices.length > 1
						? `Assign ${platform} ${type}s to Organisation`
						: `Assign ${platform} ${type} to Organisation`
				}
				open={open}
				onCancel={handleCancel}
				width={500}
				destroyOnClose
				styles={{
					content: { padding: `${marginXL}px` },
					body: { marginTop: marginXL },
					footer: { marginTop: marginXL }
				}}
				footer={[
					<Flex gap={4} justify='flex-end'>
						<Button onClick={handleCancel} disabled={saveLoading}>
							Cancel
						</Button>
						<Button
							loading={saveLoading}
							disabled={
								saveLoading ||
								loadingSubcategories ||
								!form.getFieldValue('categoryID') ||
								devices.length === 0
							}
							onClick={handleSubmit}
							type='primary'
						>
							Assign
						</Button>
					</Flex>
				]}
			>
				<Form form={form} layout='vertical' onValuesChange={handleChangeValues}>
					<Flex vertical gap={16}>
						<Form.Item name='selectedSpace' label='Select Organisation'>
							<Select
								disabled={loadingSubcategories}
								showSearch
								placeholder='Type'
								style={{ width: '100%' }}
								options={workspaces.map((space) => ({
									value: space.orgID,
									label: space.name
								}))}
								filterOption={(input, option) =>
									option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
							/>
						</Form.Item>

						{form.getFieldValue('selectedSpace') && (
							<Form.Item name='categoryType' label='Category Type'>
								<Radio.Group
									disabled={loadingSubcategories}
									value={form.getFieldValue('categoryType')}
									onChange={(e) => {
										form.setFieldsValue({
											categoryType: e.target.value,
											categoryID: null
										});
									}}
								>
									{categories.map((category) => (
										<Radio key={category.categoryID} value={category.categoryID}>
											{category.name}
										</Radio>
									))}
								</Radio.Group>
							</Form.Item>
						)}

						{subCategoryType.length > 0 &&
							form.getFieldValue('selectedSpace') &&
							form.getFieldValue('categoryType') && (
								<Form.Item name='categoryID' label='Category'>
									<Select
										placeholder='Type'
										style={{ width: '100%' }}
										options={subCategoryType.map((category) => ({
											value: category.categoryID,
											label: category.name
										}))}
									/>
								</Form.Item>
							)}

						{form.getFieldValue('categoryType') &&
							subCategoryType.length === 0 &&
							(isLoadingTemplates ? (
								<Flex justify='center' align='center'>
									<Loader />
								</Flex>
							) : (
								<Flex vertical gap={8} style={{ marginBottom: 16 }}>
									<Text type='secondary' style={{ fontSize: 13, textAlign: 'center' }}>
										Ohh... looks like this organisation doesn't have any templates to store the
										selected devices.
									</Text>
									<Flex justify='center'>
										<Button
											type='primary'
											onClick={() => {
												setOpenCreateCategoryModal(true);
											}}
										>
											Add Template
										</Button>
									</Flex>
								</Flex>
							))}

						<Flex vertical gap={8}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: 13, fontWeight: 200 }}>Selected Devices</Text>
							</Divider>
							<Flex wrap='wrap' gap={6}>
								{devices.length > 0 ? (
									devices.map((device) => (
										<Tag
											key={device.inventoryUID}
											closable
											onClose={() =>
												setDevices((prev) =>
													prev.filter((d) => d.inventoryUID !== device.inventoryUID)
												)
											}
											icon={<LockOutlined />}
											color='gold'
										>
											{device.name}
										</Tag>
									))
								) : (
									<Text type='secondary' style={{ fontSize: 13 }}>
										No devices selected
									</Text>
								)}
							</Flex>
						</Flex>
					</Flex>
				</Form>
			</Modal>
			<AddDefaultCategoryModal
				open={openCreateCategoryModal}
				setOpen={setOpenCreateCategoryModal}
				orgID={form.getFieldValue('selectedSpace')}
				refetch={() =>
					fetchSubCategories(form.getFieldValue('selectedSpace'), form.getFieldValue('categoryType'))
				}
				onLoadingChange={(val) => setCreatingCategory(val)}
			>
				<div />
			</AddDefaultCategoryModal>
		</Fragment>
	);
};

export default AssignInventoryAssets;
